import React, { useState, useEffect, useCallback } from 'react';
import { imageCacheService } from '../../services/imageCacheService';
import './CachedImage.css';

/**
 * CachedImage Component
 * Displays thumbnails by default and loads full images on demand
 *
 * Props:
 * - src: Full resolution image URL
 * - thumbnailSrc: Thumbnail image URL (optional, falls back to src)
 * - alt: Alt text for the image
 * - className: Additional CSS classes
 * - onClick: Click handler
 * - showFull: If true, loads and displays full resolution image
 * - style: Inline styles
 */
const CachedImage = ({
  src,
  thumbnailSrc,
  alt = '',
  className = '',
  onClick,
  showFull = false,
  style = {},
  aspectRatio,
  ...props
}) => {
  const wrapperStyle = aspectRatio
    ? { aspectRatio: String(aspectRatio), ...style }
    : style;
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Determine which URL to load
  const targetUrl = showFull ? src : (thumbnailSrc || src);

  const loadImage = useCallback(async () => {
    if (!targetUrl) {
      setIsLoading(false);
      setHasError(true);
      return;
    }

    setIsLoading(true);
    setHasError(false);

    try {
      // Try to get from IndexedDB cache
      const cachedBlob = await imageCacheService.getCachedImage(targetUrl);

      if (cachedBlob) {
        const objectUrl = URL.createObjectURL(cachedBlob);
        setImageSrc(objectUrl);
        setIsLoading(false);
        return;
      }

      // Fetch from network
      const response = await fetch(targetUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }

      const blob = await response.blob();

      // Cache the image
      await imageCacheService.cacheImage(targetUrl, blob);

      const objectUrl = URL.createObjectURL(blob);
      setImageSrc(objectUrl);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading cached image:', error);
      // Fallback to direct URL
      setImageSrc(targetUrl);
      setIsLoading(false);
    }
  }, [targetUrl]);

  useEffect(() => {
    loadImage();
  }, [loadImage]);

  // Cleanup when imageSrc changes
  useEffect(() => {
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [imageSrc]);

  const handleError = () => {
    setHasError(true);
    // Try falling back to direct URL
    if (imageSrc !== targetUrl) {
      setImageSrc(targetUrl);
    }
  };

  if (hasError && imageSrc === targetUrl) {
    return (
      <div
        className={`cached-image-error ${className}`}
        style={style}
        {...props}
      >
        <span>Failed to load image</span>
      </div>
    );
  }

  return (
    <div
      className={`cached-image-wrapper ${aspectRatio ? 'has-aspect' : ''} ${className}`}
      style={wrapperStyle}
    >
      {isLoading && (
        <div className="cached-image-loading">
          <div className="loading-spinner"></div>
        </div>
      )}
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          onClick={onClick}
          onError={handleError}
          onLoad={() => setIsLoading(false)}
          className={`cached-image ${isLoading ? 'loading' : 'loaded'}`}
          {...props}
        />
      )}
    </div>
  );
};

export default CachedImage;
